/**
 * TimeWeatherBadge (Time & Weather System R2.4/R3.5) — a thin, READ-ONLY
 * projection of the campaign's in-world clock + day-phase + current weather.
 * Pure consumer: it formats the {@link CampaignState} the play screen already
 * fetched from `GET /campaigns` (no new request). That read path PROJECTS the
 * state for the member (`projectCampaignForMember`, two-model gate Medium #12):
 * the `weatherLog` is trimmed server-side to the entries currently in effect, so
 * the badge is not relying on an un-filtered full history — but it is still raw
 * campaign state scoped to a confirmed member, NOT a per-player fog-of-war view,
 * so this component must only surface player-safe facts (the clock + the current
 * weather KIND), never, e.g., iterate region nodeIds into the DOM. The clock
 * derivation is the SHARED `@rufus/shared-types` formatter, so the web renders
 * the exact same date/day-phase the engine does (incl. negative/time-travel).
 */

import type { CampaignState, WeatherKind } from "@rufus/shared-types";
import { CAMPAIGN_WIDE_REGION, formatClock, dayPhase } from "@rufus/shared-types";

/** A short player-facing label per day-phase. */
const DAY_PHASE_LABEL: Record<string, string> = {
  dawn: "Dawn",
  day: "Day",
  dusk: "Dusk",
  night: "Night",
};

/** A short player-facing label per weather kind. */
const WEATHER_LABEL: Record<WeatherKind, string> = {
  clear: "Clear",
  cloudy: "Cloudy",
  rain: "Rain",
  storm: "Storm",
  fog: "Fog",
  snow: "Snow",
  wind: "Windy",
};

/**
 * The current weather to show the player: the entry whose interval
 * `[startMin, endMin)` CONTAINS the current clock minute for the party's region,
 * falling back to the campaign-wide entry. Uses interval containment (not
 * open-endedness) so it matches the server's `currentWeather` after a time-travel
 * jump (two-model gate Medium #6). The badge has no spatial graph client-side, so
 * it resolves "the party's region" as the party's exact location id and otherwise
 * shows campaign-wide.
 *
 * KNOWN DISPLAY LIMITATION (accepted): the SERVER resolves the party's Macro/Meso
 * ANCESTOR region (via `partyRegionId`, which `set_weather` also uses to default
 * its region), but the badge cannot walk the hierarchy without the graph. So when
 * the party stands in a Micro node and the DM set region-defaulted weather, the
 * DM narration reads it back correctly (server-side) while this badge falls back
 * to campaign-wide. The player still experiences the weather through the DM's
 * prose; closing the gap would require threading the resolved region id onto the
 * projected CampaignState wire payload — a tracked follow-on, not built here.
 */
function currentWeatherFor(campaign: CampaignState): WeatherKind | undefined {
  const now = campaign.worldClock.minuteOfEpoch;
  const log = campaign.weatherLog ?? [];
  const inEffectFor = (regionId: string) =>
    log.find((e) => e.regionId === regionId && e.startMin <= now && (e.endMin === undefined || now < e.endMin));
  const party = campaign.partyLocation ? inEffectFor(campaign.partyLocation) : undefined;
  return (party ?? inEffectFor(CAMPAIGN_WIDE_REGION))?.kind;
}

export interface TimeWeatherBadgeProps {
  readonly campaign: CampaignState | undefined;
}

/**
 * A compact badge: "Day 412, 14:30 · Day · Rain". Renders nothing when there is
 * no campaign loaded yet. Weather is omitted when unset (benign default — the
 * clock + day-phase always show once a campaign is loaded).
 */
export function TimeWeatherBadge({ campaign }: TimeWeatherBadgeProps) {
  if (!campaign) return null;
  const phase = dayPhase(campaign.worldClock);
  const weather = currentWeatherFor(campaign);
  return (
    <div className="time-weather-badge" data-testid="time-weather-badge" aria-label="in-world time and weather">
      <span data-testid="tw-clock">{formatClock(campaign.worldClock)}</span>
      <span aria-hidden="true"> · </span>
      <span data-testid="tw-phase">{DAY_PHASE_LABEL[phase]}</span>
      {weather ? (
        <>
          <span aria-hidden="true"> · </span>
          <span data-testid="tw-weather">{WEATHER_LABEL[weather]}</span>
        </>
      ) : null}
    </div>
  );
}
